import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBlueprint } from '../contexts/BlueprintContext';
import { useTour } from '../contexts/TourContext';
import {
  getAllBlueprints,
  getBlueprintWorkflow,
  getBlueprintCategories,
  getBlueprintById,
  recordBlueprintView,
} from '../services/blueprintsApi';
import { 
  BookOpen, Search, Filter, ChevronRight, ChevronDown, 
  Smartphone, Clock, Eye, Grid, List, ArrowRight, ArrowLeft,
  CheckCircle2, Lightbulb, ExternalLink, Home, Play, Video, Sparkles
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { TourLauncher, InteractiveTutorial } from '../components/Tour';
import HelpIcon from '../components/HelpIcon';


// Mapping of Blueprint IDs to their corresponding Interactive Tutorial IDs
const BLUEPRINT_TO_TUTORIAL_MAP = {
  // Getting Started
  'dashboard-overview': 'welcome-dashboard',
  'my-desk-tasks': 'welcome-dashboard',
  'my-desk-calendar': 'welcome-dashboard',
  'notifications': 'welcome-dashboard',
  'resources': 'welcome-dashboard',
  
  // Projects
  'projects-list': 'creating-projects',
  'project-hub': 'creating-projects',
  'project-info': 'creating-projects',
  'journey-phases': 'creating-projects',
  'journey-tab-phases': 'creating-projects',
  'project-progress': 'creating-projects',
  'project-activity-log': 'creating-projects',
  'client-surveys': 'creating-projects',
  'scope-statements': 'creating-projects',
  
  // Clips & Design
  'clips-library': 'clips-mastery',
  'clips-materials': 'clips-mastery',
  'clips-tab-materials': 'clips-mastery',
  'web-clipper': 'browser-extension',
  
  // Financials
  'financials': 'financials-mastery',
  'estimates': 'financials-mastery',
  'invoices': 'financials-mastery',
  'budget-tracker': 'financials-mastery',
  'estimates-tab': 'financials-mastery',
  'project-invoices-tab': 'financials-mastery',
  
  // RFPs & Procurement
  'rfps-and-procurement': 'rfp-workflow',
  'project-procurement': 'rfp-workflow',
  
  // Change Orders
  'change-orders': 'change-orders',
  'project-change-orders-tab': 'change-orders',
  
  // Contractors
  'contractors': 'contractor-management',
  'contractor-portal': 'contractor-management',
  'site-office': 'contractor-management',
  
  // Client Portals
  'drawing-room': 'drawing-room',
  'portals-tab': 'drawing-room',
  'project-messages': 'drawing-room',
  
  // Documents
  'document-templates': 'documents',
  'documents-tab': 'documents',
  'project-documents': 'documents',
  
  // Admin
  'phase-templates-admin': 'creating-projects',
  'work-tables': 'clips-mastery',
};

const BlueprintLibrary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchBlueprintById } = useBlueprint();
  const { completedChapters, chapters, hasSeenTour } = useTour();
  const [blueprints, setBlueprints] = useState([]);
  const [workflowPhases, setWorkflowPhases] = useState({});
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPhases, setExpandedPhases] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [viewMode, setViewMode] = useState('workflow'); // 'workflow' | 'category'
  const [loading, setLoading] = useState(true);
  const [selectedBlueprint, setSelectedBlueprint] = useState(null);
  const [showTourLauncher, setShowTourLauncher] = useState(false);
  const [activeTutorialId, setActiveTutorialId] = useState(null);

  // Get the tutorial ID for the current blueprint
  const getRelatedTutorial = (blueprintId) => {
    return BLUEPRINT_TO_TUTORIAL_MAP[blueprintId] || null;
  };

  // Check if we're on the glossary route
  const isGlossaryRoute = location.pathname === '/glossary';

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-load glossary if on /glossary route
  useEffect(() => {
    if (isGlossaryRoute && blueprints.length > 0 && !selectedBlueprint) {
      const glossary = blueprints.find(bp => bp.id === 'key-made-terms-glossary' || bp.title?.toLowerCase().includes('glossary'));
      if (glossary) {
        handleSelectBlueprint(glossary);
      }
    }
  }, [isGlossaryRoute, blueprints]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // FIX 9 — shared service: session-gated, deduped, configured axios client.
      const [all, workflow, cats] = await Promise.all([
        getAllBlueprints(),
        getBlueprintWorkflow(),
        getBlueprintCategories()
      ]);
      
      setBlueprints(all);
      setWorkflowPhases(workflow);
      setCategories(cats);
      
      // Expand first phase by default
      const phases = Object.keys(workflow || {});
      if (phases.length > 0) {
        setExpandedPhases({ [phases[0]]: true });
      }
    } catch (error) {
      console.error('Error fetching blueprints:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePhase = (phase) => {
    setExpandedPhases(prev => ({ ...prev, [phase]: !prev[phase] }));
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const filteredBlueprints = searchQuery
    ? blueprints.filter(bp =>
        bp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bp.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bp.content?.overview?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : blueprints;

  const handleSelectBlueprint = async (bp) => {
    // Fetch full blueprint data
    try {
      const full = await getBlueprintById(bp.id);
      setSelectedBlueprint(full || bp);
      // Increment view count
      recordBlueprintView(bp.id);
    } catch {
      setSelectedBlueprint(bp);
    }
  };

  const handleBackToList = () => {
    setSelectedBlueprint(null);
  };

  // Group by category
  const blueprintsByCategory = filteredBlueprints.reduce((acc, bp) => {
    const cat = bp.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(bp);
    return acc;
  }, {});

  // Workflow phases in order
  const workflowOrder = [
    'Getting Started',
    'Project Setup', 
    'Design Phase',
    'Client Interaction',
    'Construction',
    'Financial',
    'Administration',
    'Uncategorized'
  ];

  const sortedPhases = Object.keys(workflowPhases).sort((a, b) => {
    const aIdx = workflowOrder.indexOf(a);
    const bIdx = workflowOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  // Find related blueprints for selected
  const getRelatedBlueprints = () => {
    if (!selectedBlueprint?.content?.related) return [];
    return selectedBlueprint.content.related
      .map(id => blueprints.find(bp => bp.id === id))
      .filter(Boolean);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header with Breadcrumb */}
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm mb-2">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 hover:text-[#70170F] flex items-center gap-1"
            >
              <Home className="w-4 h-4" />
              My Desk
            </button>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <button 
              onClick={handleBackToList}
              className={`${selectedBlueprint ? 'text-gray-500 hover:text-[#70170F]' : 'text-gray-900 dark:text-white font-medium'}`}
            >
              Blueprint Library
            </button>
            {selectedBlueprint && (
              <>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900 dark:text-white font-medium">{selectedBlueprint.title}</span>
              </>
            )}
          </div>
          
          {/* Title row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {selectedBlueprint && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleBackToList}
                  className="mr-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              <BookOpen className="w-6 h-6 text-[#70170F]" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {selectedBlueprint ? selectedBlueprint.title : 'Blueprint Library'}
              </h1>
              {!selectedBlueprint && <HelpIcon elementId="blueprint-library" position="inline" />}
              {selectedBlueprint?.phoneFriendly && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  <Smartphone className="w-3 h-3 mr-1" />
                  Phone Friendly
                </Badge>
              )}
            </div>
            
            {!selectedBlueprint && (
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search blueprints..."
                    className="pl-9 w-64"
                  />
                </div>
                <span className="text-sm text-gray-500">{filteredBlueprints.length} blueprints</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#70170F] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : selectedBlueprint ? (
          /* Inline Blueprint Viewer */
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Overview */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-start gap-4 mb-4">
                  <span className="text-4xl">{selectedBlueprint.icon}</span>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedBlueprint.title}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary">{selectedBlueprint.category}</Badge>
                      {selectedBlueprint.subcategory && (
                        <Badge variant="outline">{selectedBlueprint.subcategory}</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {selectedBlueprint.content?.overview}
                </p>
                
                {/* Watch Tutorial Button - Shows if there's a related interactive tutorial */}
                {getRelatedTutorial(selectedBlueprint.id) && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-[#70170F]/10 to-[#70170F]/5 rounded-lg border border-[#70170F]/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#70170F] flex items-center justify-center">
                          <Play className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">Interactive Tutorial Available</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Watch a guided walkthrough with real screenshots</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => setActiveTutorialId(getRelatedTutorial(selectedBlueprint.id))}
                        className="bg-[#70170F] hover:bg-[#8B1E14] text-white"
                        data-testid="watch-tutorial-btn"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Watch Tutorial
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Start */}
              {selectedBlueprint.content?.quickStart?.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Quick Start
                  </h3>
                  <div className="space-y-3">
                    {selectedBlueprint.content.quickStart.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#70170F] text-white text-sm flex items-center justify-center font-medium">
                          {item.step || idx + 1}
                        </span>
                        <p className="text-gray-700 dark:text-gray-300 pt-0.5">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Content Sections */}
              {selectedBlueprint.content?.sections?.length > 0 && (
                <div className="space-y-4">
                  {selectedBlueprint.content.sections.map((section, idx) => (
                    <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        {section.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">{section.content}</p>
                      {section.tips?.length > 0 && (
                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 font-medium mb-2">
                            <Lightbulb className="w-4 h-4" />
                            Tips
                          </div>
                          <ul className="space-y-1 text-amber-900 dark:text-amber-200 text-sm">
                            {section.tips.map((tip, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-amber-600">•</span>
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Pro Tips */}
              {selectedBlueprint.content?.proTips?.length > 0 && (
                <div className="bg-gradient-to-r from-[#70170F]/10 to-[#70170F]/5 rounded-lg border border-[#70170F]/20 p-6">
                  <h3 className="text-lg font-semibold text-[#70170F] mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    Pro Tips
                  </h3>
                  <ul className="space-y-2">
                    {selectedBlueprint.content.proTips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                        <span className="text-[#70170F] font-bold">→</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Metadata */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Category</span>
                    <span className="text-gray-900 dark:text-white">{selectedBlueprint.category}</span>
                  </div>
                  {selectedBlueprint.subcategory && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Subcategory</span>
                      <span className="text-gray-900 dark:text-white">{selectedBlueprint.subcategory}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Views</span>
                    <span className="text-gray-900 dark:text-white flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {selectedBlueprint.meta?.views || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Phone Friendly</span>
                    <span className={selectedBlueprint.phoneFriendly ? 'text-green-600' : 'text-gray-400'}>
                      {selectedBlueprint.phoneFriendly ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Context Routes */}
              {selectedBlueprint.routes?.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Appears On</h4>
                  <div className="space-y-1.5">
                    {selectedBlueprint.routes.map((route, idx) => (
                      <div key={idx} className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-600 dark:text-gray-300 truncate">
                        {route}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Blueprints */}
              {getRelatedBlueprints().length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Related Guides</h4>
                  <div className="space-y-2">
                    {getRelatedBlueprints().map(bp => (
                      <button
                        key={bp.id}
                        onClick={() => handleSelectBlueprint(bp)}
                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                      >
                        <span className="text-lg">{bp.icon}</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{bp.title}</span>
                        <ArrowRight className="w-3 h-3 ml-auto text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Blueprint List View */
          <div className="space-y-6">
            {/* Start Tour & KEY-MADE Overview Section */}
            <div className="bg-gradient-to-r from-[#70170F] to-[#8B1E14] rounded-lg p-6 mb-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold mb-2">Welcome to KEY-MADE Blueprint Library</h2>
                  <p className="text-white/90 text-sm">Your comprehensive guide to mastering the KEY-MADE platform</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={() => setShowTourLauncher(true)}
                    className="bg-white text-[#70170F] hover:bg-gray-100 font-semibold px-6 py-2"
                    data-testid="start-tour-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {hasSeenTour ? 'Continue Tour' : 'Start Guided Tour'}
                  </Button>
                </div>
              </div>
              
              {/* Tour Progress Indicator */}
              {hasSeenTour && completedChapters.length > 0 && (
                <div className="mb-4 bg-white/10 rounded-lg p-3">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-white/80">Your Tour Progress</span>
                    <span className="text-white font-medium">{completedChapters.length}/{chapters.length} chapters</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-white rounded-full transition-all duration-500"
                      style={{ width: `${(completedChapters.length / chapters.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">🏠</span>
                  </div>
                  <div>
                    <div className="font-medium mb-1">Dashboard Mastery</div>
                    <div className="text-white/80 text-xs">Navigate your workspace like a pro</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">📋</span>
                  </div>
                  <div>
                    <div className="font-medium mb-1">Project Workflows</div>
                    <div className="text-white/80 text-xs">Streamline your project management</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">🎯</span>
                  </div>
                  <div>
                    <div className="font-medium mb-1">Best Practices</div>
                    <div className="text-white/80 text-xs">Learn from industry experts</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Featured/Quick Access Section */}
            <div className="bg-gradient-to-r from-[#70170F]/5 to-transparent rounded-lg border border-[#70170F]/20 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-[#70170F]" />
                Quick Access
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Terms Glossary - Always first */}
                {blueprints.find(bp => bp.id === 'key-made-terms-glossary' || bp.title?.toLowerCase().includes('glossary')) && (
                  <button
                    onClick={() => handleSelectBlueprint(blueprints.find(bp => bp.id === 'key-made-terms-glossary' || bp.title?.toLowerCase().includes('glossary')))}
                    className="flex items-center gap-3 p-4 rounded-lg border-2 border-[#70170F]/30 bg-white dark:bg-gray-800 hover:border-[#70170F] hover:shadow-md transition-all text-left"
                  >
                    <span className="text-2xl">📖</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#70170F] text-sm">Terms Glossary</div>
                      <div className="text-xs text-gray-500">Learn KEY-MADE terminology</div>
                    </div>
                  </button>
                )}
                {/* Dashboard Overview */}
                {blueprints.find(bp => bp.id === 'dashboard-overview') && (
                  <button
                    onClick={() => handleSelectBlueprint(blueprints.find(bp => bp.id === 'dashboard-overview'))}
                    className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-[#70170F] hover:shadow-md transition-all text-left"
                  >
                    <span className="text-2xl">🏠</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-white text-sm">Getting Started</div>
                      <div className="text-xs text-gray-500">Dashboard overview</div>
                    </div>
                  </button>
                )}
                {/* Project Hub */}
                {blueprints.find(bp => bp.id === 'project-hub') && (
                  <button
                    onClick={() => handleSelectBlueprint(blueprints.find(bp => bp.id === 'project-hub'))}
                    className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-[#70170F] hover:shadow-md transition-all text-left"
                  >
                    <span className="text-2xl">📋</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-white text-sm">Project Hub</div>
                      <div className="text-xs text-gray-500">Central project workspace</div>
                    </div>
                  </button>
                )}
                {/* Drawing Room */}
                {blueprints.find(bp => bp.id === 'drawing-room') && (
                  <button
                    onClick={() => handleSelectBlueprint(blueprints.find(bp => bp.id === 'drawing-room'))}
                    className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-[#70170F] hover:shadow-md transition-all text-left"
                  >
                    <span className="text-2xl">🖼️</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-white text-sm">Drawing Room</div>
                      <div className="text-xs text-gray-500">Client portal guide</div>
                    </div>
                  </button>
                )}
              </div>
            </div>

            <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList className="mb-6">
              <TabsTrigger value="workflow" className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4" />
                Workflow Order
              </TabsTrigger>
              <TabsTrigger value="category" className="flex items-center gap-2">
                <Grid className="w-4 h-4" />
                By Category
              </TabsTrigger>
            </TabsList>

            {/* Workflow View */}
            <TabsContent value="workflow" className="space-y-4">
              {sortedPhases.map(phase => {
                const phaseBlueprints = workflowPhases[phase] || [];
                const isExpanded = expandedPhases[phase];
                
                return (
                  <div 
                    key={phase}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <button
                      onClick={() => togglePhase(phase)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                        <span className="font-semibold text-gray-900 dark:text-white">{phase}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {phaseBlueprints.length} guides
                        </span>
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                        {phaseBlueprints.map(bp => (
                          <button
                            key={bp.id}
                            onClick={() => handleSelectBlueprint(bp)}
                            className="w-full flex items-center gap-4 p-4 pl-12 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                            data-testid={`blueprint-item-${bp.id}`}
                          >
                            <span className="text-2xl">{bp.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 dark:text-white">{bp.title}</span>
                                {bp.phoneFriendly && (
                                  <span className="flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
                                    <Smartphone className="w-3 h-3" />
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                {bp.content?.overview?.slice(0, 100)}...
                              </p>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <Eye className="w-3 h-3" />
                              {bp.meta?.views || 0}
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {sortedPhases.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No blueprints found.</p>
                  <p className="text-sm mt-1">Blueprints will appear here as they&apos;re created.</p>
                </div>
              )}
            </TabsContent>

            {/* Category View */}
            <TabsContent value="category">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(blueprintsByCategory).map(([category, bps]) => (
                  <div 
                    key={category}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 dark:text-white">{category}</span>
                      <span className="text-sm text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                        {bps.length}
                      </span>
                    </button>
                    
                    <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                      {bps.map(bp => (
                        <button
                          key={bp.id}
                          onClick={() => handleSelectBlueprint(bp)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                        >
                          <span className="text-lg">{bp.icon}</span>
                          <span className="flex-1 text-sm text-gray-700 dark:text-gray-300">{bp.title}</span>
                          {bp.phoneFriendly && (
                            <Smartphone className="w-3 h-3 text-blue-500" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {Object.keys(blueprintsByCategory).length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No blueprints found.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
          </div>
        )}
      </div>
      
      {/* Tour Launcher Modal */}
      <TourLauncher open={showTourLauncher} onOpenChange={setShowTourLauncher} />
      
      {/* Interactive Tutorial - Shows when a tutorial is active */}
      {activeTutorialId && (
        <InteractiveTutorial
          tutorialId={activeTutorialId}
          onClose={() => setActiveTutorialId(null)}
          onComplete={() => setActiveTutorialId(null)}
          startingRoute="/blueprints"
        />
      )}
    </div>
  );
};

export default BlueprintLibrary;
